import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import { parse as parseQueryStringParams } from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { useStripePublishableKeyByToken } from '../../../../queries';
import { useClientCalculateDonationFee } from '../../../../queries/Surveys/useClientCalculateDonationFee';
import { useClientGetStripeCheckoutSession } from '../../../../queries/Surveys/useClientGetStripeCheckoutSession';
import { useClientStripeSessionByToken } from '../../../../queries/Surveys/useClientStripeSessionByToken';
import { DonationFrequency, IDonationSurvey, IDonationSurveyResponse } from '../../../models/Api';
import { formatCentsAsDollars } from '../../../models/currency';
import { migrateLegacyDonationOptions } from '../../../models/DonationSurveyMigration';
import { LoadingLogo } from '../../../../web/components/LoadingLogo';
import { DonationOrderSummary } from '../../PaymentPage/DonationOrderSummary';
import { PaymentPage } from '../../PaymentPage';
import { CheckoutFormProviderWrap } from '../../StripeCheckout';
import { DonationForm } from './DonationForm';
import { ThankYouMessage } from './ThankYou';
import { styleSheet } from './styles';
import { IFormValues, donationSurveyResponseValidationSchema } from './utils';

interface IProps {
	survey: IDonationSurvey;
	token: string;
}

interface DonationCheckoutContentProps {
	donationResponse: IDonationSurveyResponse;
	stripePublicId: string;
	token: string;
	onSuccessClose: (sessionId: string) => void;
	isProcessingPayment: boolean;
}

export const DonationCheckoutContent = ({
	donationResponse,
	stripePublicId,
	token,
	onSuccessClose,
	isProcessingPayment,
}: DonationCheckoutContentProps) => {
	const stripeSession = useClientStripeSessionByToken();
	const clientSecretPromise = useMemo(() => {
		return stripeSession.mutateAsync({
			token,
			donationAmount: donationResponse.donationAmount,
			serviceFeeAmount: donationResponse.serviceFee,
			donorCoversFees: donationResponse.donorCoversFees,
			response: donationResponse,
			isRecurring: donationResponse.donationFrequency !== null && donationResponse.donationFrequency !== undefined,
			donationFrequency: donationResponse.donationFrequency,
			_type: 'DonationSurveyCheckout',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const defaultValues = useMemo(
		() => ({
			billingAddress: {
				name: donationResponse?.donor
					? `${donationResponse.donor.firstName} ${donationResponse.donor.lastName}`
					: undefined,
				address: {
					line1: donationResponse?.donor?.address?.address1,
					line2: donationResponse?.donor?.address?.address2,
					city: donationResponse?.donor?.address?.city,
					state: donationResponse?.donor?.address?.stateProvince,
					postal_code: donationResponse?.donor?.address?.postalCode,
					country: donationResponse?.donor?.address?.country || 'US',
				},
			},
		}),
		[donationResponse]
	);

	const totalAmount = donationResponse.donorCoversFees
		? donationResponse.donationAmount + donationResponse.serviceFee
		: donationResponse.donationAmount;

	return (
		<CheckoutFormProviderWrap
			stripePublicId={stripePublicId}
			clientSecretPromise={clientSecretPromise}
			defaultValues={defaultValues}
			prefilledEmail={donationResponse?.donor?.email ?? ''}
			onSuccessClose={onSuccessClose}
			submitButtonLabel={`Donate $${formatCentsAsDollars(totalAmount)}`}
			loggingName='DonationSurveyCheckoutForm'
			isProcessingPayment={isProcessingPayment}
		/>
	);
};

export const DonationSurvey = ({ survey, token }: IProps) => {
	const history = useHistory<{ search?: { checkoutStarted?: boolean } }>();
	const [showCheckoutForm, setShowCheckoutForm] = useState<boolean>(false);
	const [donationResponse, setDonationResponse] = useState<IDonationSurveyResponse | null>(null);
	/**
	 * Parse sessionId from URL query params on initial load.
	 * When Stripe redirects back after payment (e.g. 3DS on mobile), the page reloads
	 * and all React state is lost. The sessionId in the URL allows us to recover
	 * and complete the checkout flow by querying the checkout session endpoint.
	 */
	const [stripeSessionId, setStripeSessionId] = useState<string | null>(() => {
		const queryObject = parseQueryStringParams(history.location.search);
		if (queryObject.sessionId && typeof queryObject.sessionId === 'string') {
			return queryObject.sessionId;
		}
		return null;
	});
	const [isProcessingPayment, setIsProcessingPayment] = useState<boolean>(false);
	const { data: checkoutSession, isLoading: isCheckoutSessionLoading } = useClientGetStripeCheckoutSession({
		sessionId: stripeSessionId,
		refetchOnWindowFocus: false,
		token,
	});

	/** Clean sessionId from URL after it's been parsed */
	useEffect(() => {
		if (stripeSessionId) {
			history.replace({
				pathname: history.location.pathname,
				search: '',
			});
		}
	}, [stripeSessionId, history]);

	const { data: stripePublicId } = useStripePublishableKeyByToken({
		accessToken: token,
		refetchOnWindowFocus: false,
	});

	// Migrate legacy donation options to new frequency-specific structure
	const migratedSurvey = useMemo(() => {
		return {
			...survey,
			donationOptions: migrateLegacyDonationOptions(
				survey.donationOptions,
				survey.donationOptions?.allowRecurringDonations || false
			),
		};
	}, [survey]);

	// Get the one-time donation settings for default values
	const oneTimeSettings = migratedSurvey.donationOptions.oneTimeDonationSettings;

	const defaultValues: IFormValues = useMemo(() => {
		return {
			amount:
				oneTimeSettings?.enableSuggestedAmounts && oneTimeSettings.suggestedAmounts[0]
					? oneTimeSettings.suggestedAmounts[0]
					: null,
			customAmount: null as number | null,
			campaignId: '',
			coverFees: false,
			donationFrequency: null as DonationFrequency | null,
			address: {
				address1: '',
				address2: '',
				city: '',
				stateProvince: '',
				postalCode: '',
				country: '',
			},
			email: '',
			phone: '',
			firstName: '',
			lastName: '',
			customProperties: {},
		};
	}, [oneTimeSettings]);

	const validationSchema = useMemo(() => {
		return donationSurveyResponseValidationSchema(survey);
	}, [survey]);

	const { setValue, watch, handleSubmit, control, formState } = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const formValues = watch();

	const { data: fees, isLoading: isLoadingFees } = useClientCalculateDonationFee({
		surveyId: survey.id,
		value: formValues.customAmount ? formValues.customAmount * 100 : formValues.amount * 100,
	});

	const onSubmit = useCallback(
		async (data: IFormValues) => {
			if (!fees) {
				return;
			}

			const response: IDonationSurveyResponse = {
				donationAmount: fees.donationAmount,
				serviceFee: data.coverFees ? fees.serviceFee : 0,
				totalCharge: data.coverFees ? fees.totalCharge : fees.donationAmount,
				donorCoversFees: data.coverFees,
				selectedCampaignId: data.campaignId,
				confirmationNumber: '',
				donor: {
					firstName: data.firstName || '',
					lastName: data.lastName || '',
					email: data.email || '',
					phone: data.phone || '',
					address: {
						city: data.address.city,
						country: data.address.country,
						postalCode: data.address.postalCode,
						stateProvince: data.address.stateProvince,
						address1: data.address.address1,
						address2: data.address.address2,
					},
				},
				customProperties: data.customProperties,
				donationFrequency: data.donationFrequency,
				isMirrored: false,
				token,
				surveyId: survey.id,
			};

			setDonationResponse(response);
			setShowCheckoutForm(true);
		},
		[fees, survey.id, token]
	);

	const handleCloseCheckoutForm = useCallback(() => {
		setShowCheckoutForm(false);
	}, []);

	const onSuccessCloseCheckoutForm = useCallback(
		(sessionId: string) => {
			setStripeSessionId(sessionId);
			setIsProcessingPayment(true);

			history.replace({
				pathname: `/donation/${token}`,
				search: '',
			});
		},
		[history, token]
	);

	useEffect(() => {
		if (checkoutSession && isProcessingPayment) {
			setShowCheckoutForm(false);
			setIsProcessingPayment(false);
			if (window) {
				window.onbeforeunload = null;
			}
		}
	}, [checkoutSession, isProcessingPayment]);

	if (checkoutSession) {
		return <ThankYouMessage />;
	}

	return (
		<div className={css(styleSheet.container)}>
			{stripeSessionId && isCheckoutSessionLoading && (
				<section
					className={css(styleSheet.processingOverlay)}
					role='dialog'
					aria-modal='true'
					aria-labelledby='processing-title'
				>
					<LoadingLogo size={80} />
					<div>
						<h2 id='processing-title' className={css(styleSheet.processingMessage)}>
							Processing your donation...
						</h2>
						<p className={css(styleSheet.processingMessage)} aria-live='polite'>
							Please do not close or refresh this window.
						</p>
					</div>
				</section>
			)}
			{showCheckoutForm && donationResponse && stripePublicId ? (
				<PaymentPage
					title='Complete Donation'
					onClose={handleCloseCheckoutForm}
					summaryContent={<DonationOrderSummary donationResponse={donationResponse} />}
				>
					<DonationCheckoutContent
						donationResponse={donationResponse}
						stripePublicId={stripePublicId}
						token={token}
						onSuccessClose={onSuccessCloseCheckoutForm}
						isProcessingPayment={isProcessingPayment}
					/>
				</PaymentPage>
			) : (
				<DonationForm
					survey={migratedSurvey}
					formValues={formValues}
					setValue={setValue}
					onSubmit={handleSubmit(onSubmit)}
					control={control}
					formState={formState}
					isLoadingFees={isLoadingFees}
					isLoadingCheckoutSession={isCheckoutSessionLoading}
					token={token}
				/>
			)}
		</div>
	);
};
